import React, { useRef } from 'react'
import Box from '@mui/material/Box'
import MuiModal from '@mui/material/Modal'
import IconButton from '@/shared/components/IconButton/IconButton'
import { modalBackgroundStyles, modalStyles } from './Modal.styles'
import styles from './Modal.module.scss'

type ModalSize = 'small' | 'medium' | 'large' | 'xlarge' | 'fit-content'

interface ModalProps {
  children: React.ReactNode
  onClose?: () => void
  size?: ModalSize
  /** Usually a string; settings sets a second heading on the same line. */
  title: React.ReactNode
  isOpen: boolean
  zIndex?: number
  /**
   * Kept on by default, as every existing caller expects. Turn it off for a
   * modal opened from inside another one: the outer modal's Box is centred with
   * a transform, and a transform is a containing block for `position: fixed`
   * descendants, so an in-place nested modal would centre itself inside that
   * box and its backdrop would only cover it.
   */
  disablePortal?: boolean
}

function Modal(props: ModalProps) {
  const {
    children,
    onClose,
    size = 'small',
    title,
    isOpen,
    zIndex,
    disablePortal = true
  } = props

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  /*
    Whether a list, a picker or an options tile was open when the press began.
    Those float in poppers with no backdrop of their own, so a click beside
    one lands on this modal's backdrop; it should close the popper and stop
    there, not take the whole dialog with it. Read on the press, because by
    the click the popper has already closed on blur.
  */
  const popperOpenOnPress = useRef(false)

  const getWidth = (size: ModalSize) => {
    switch (size) {
      case 'small':
        return '400px'
      case 'medium':
        return '640px'
      case 'large':
        return '800px'
      // A settings dialog with its own navigation column, as Claude's is. Sized
      // by its widest section, the restaurant's: the address's four columns
      // keep "Lokal lub piętro" and its example legible at this width.
      case 'xlarge':
        return '880px'
      case 'fit-content':
        return 'fit-content'
      default:
        return '600px'
    }
  }

  return (
    <MuiModal
      disablePortal={disablePortal}
      disableEnforceFocus
      disableAutoFocus
      open={isOpen}
      /*
        A click outside closes the way the X does, through the caller's
        `onClose`, so a dialog that asks before dropping unsaved changes asks
        here too. A text selection dragged out of a field and let go over the
        backdrop is not a click on it: the browser sends that click to the
        two elements' common ancestor.
      */
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' && popperOpenOnPress.current) return
        handleClose()
      }}
      onPointerDownCapture={() => {
        // On screen, not merely mounted: TooltipButton keeps its popper in the
        // page, hidden, and that one must not stop a click outside for good.
        popperOpenOnPress.current = Array.from(
          document.querySelectorAll('.MuiPopper-root')
        ).some(popper => popper.getClientRects().length > 0)
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={modalBackgroundStyles}
    >
      <Box sx={{ ...modalStyles, width: getWidth(size), zIndex: zIndex || 6 }}>
        <div className={styles.modalWrapper}>
          <div className={styles.modalHeader}>
            <p className={`title-bold-medium ${styles.modalTitle}`}>{title}</p>
            <IconButton
              color="primary"
              icon="las la-times"
              onClick={handleClose}
            />
          </div>
          {children}
        </div>
      </Box>
    </MuiModal>
  )
}

export default Modal
